import { Component, Input, Output, EventEmitter, IterableDiffers, ViewChild  } from "@angular/core";
import { IgxGridActionsBaseDirective, IgxIconService } from "@infragistics/igniteui-angular";

import { Command, IconCommand, LabelCommand } from "@tylertech/commands-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { isHttpResponseError } from "@tylertech/core-module";
import { IntentService } from "@tylertech/intents-module";
import { AugmentedModelObject, IAugmentedWithModel, IAugmentedWithParent, MetadataActionService, MetadataActionHelpers, MetadataViewService, RepositoryFormData } from "@tylertech/metadata-module";
import { EventEmitterHelper, StringHelper, Utilities } from "@tylertech/shared-module";
import { IInfoCardAction, IInfoTableRow, IInfoCardData, IInfoTable, IInfoTableRowActionCriteria, IMetadataAction, } from "@tylertech/tyler-api-types";

import { InfoTableCommandArgs } from "../../interfaces";
import { InfoTableService } from "../../services";
import { AugmentedTableRow, FetchOptions, InfoTableDataItem, isServerBackedInfoTable } from "./infoTable.component";
import { PopoverComponent } from "@tylertech/forge";

export interface InfoGridRowActionEvent
{
    readonly command: IInfoCardAction;
    readonly item?: IInfoCardData;
}

export interface EditRowArgs
{
    readonly row: IInfoTableRow & IAugmentedWithModel;
    readonly providerId: string;
    readonly viewOnly: boolean;
    readonly canDelete: boolean;
}

@Component({
    selector: "grid-row-actions",
    templateUrl: "./gridRowActions.tmpl.html",
    providers: [
        // NOTE - this is required for the super class to resolve it's dependencies.
        {
            provide: IgxGridActionsBaseDirective,
            useExisting: GridRowActionsComponent
        }
    ],
    standalone: false
})
export class GridRowActionsComponent extends IgxGridActionsBaseDirective
{
    @Input() private readonly providerId: string;
    @Input() private readonly disableViewAction: boolean;
    @Input() private readonly datacontroller: RepositoryFormData;
    @Input() private readonly isSearchTable: boolean;
    @Input() private readonly infoTable: IInfoTable;

    // TODO - what is the difference between customCommand and customAction?
    @Output() private readonly customAction = new EventEmitter<InfoGridRowActionEvent>();
    // TODO - what is the difference between customCommand and customAction?
    @Output() private readonly customCommand = new EventEmitter<InfoTableCommandArgs>();
    @Output("deleteRow") private readonly delete = new EventEmitter<IInfoTableRow & IAugmentedWithModel>();
    @Output("editRow") private readonly edit = new EventEmitter<EditRowArgs>();
    @Output() private readonly reloadTableData = new EventEmitter<FetchOptions>();

    @ViewChild("overFlowMenu") overFlowMenu: PopoverComponent;

    readonly commands: ReadonlyArray<Command>;

    constructor(
        iconService: IgxIconService,
        differs: IterableDiffers,
        private readonly $intentService: IntentService,
        private readonly $commonDialogsService: CommonDialogsService,
        private readonly $metadataActionService: MetadataActionService,
        private readonly $metadataViewService: MetadataViewService,
        private readonly $infoTableService: InfoTableService,
    )
    {
        super(iconService, differs);

        this.commands = [
            new IconCommand({
                icon: "edit",
                label: "Edit",
                tooltip: "Edit",
                getVisible: () => this.canEdit,
                action: ($event: Event) =>
                {
                    $event?.stopPropagation();

                    this.onEdit($event);
                }
            }),
            new IconCommand({
                icon: "delete",
                label: "Delete",
                tooltip: "Delete",
                getVisible: () => this.canDelete,
                action: ($event: Event) =>
                {
                    $event?.stopPropagation();

                    this.onDelete($event);
                }
            }),
            new IconCommand({
                icon: "visibility",
                label: "View",
                tooltip: "View",
                getVisible: () => this.canView,
                action: ($event: Event) =>
                {
                    $event?.stopPropagation();

                    this.onView($event);
                }
            })
        ];
    }

    private get context(): InfoTableDataItem | undefined
    {
        return this.isRow(this.strip.context)
            ? this.strip.context.data
            : undefined;
    }

    private get row(): AugmentedTableRow { return this.context?.$$row; }

    private get commandCard(): IInfoCardData { return this.context?.$$commandCard; }

    get canEdit(): boolean { return this.canDo("Edit"); }
    get canDelete(): boolean { return this.canDo("Delete"); }
    get canView(): boolean { return this.row && this.isViewOnly && !this.disableViewAction; }
    get isViewOnly(): boolean { return this.row && (this.infoTable?.ViewOnly || this.row?.Disabled); }
    get useLazyLoadCommands(): boolean { return isServerBackedInfoTable(this.infoTable) && this.infoTable.lazyLoadCommands; }

    get menuCommands(): ReadonlyArray<Command>
    {
        if (!this.context)
        {
            return undefined;
        }

        if (!this.context.$$commands)
        {
            this.context.$$commands = this.row?.Commands
                ?.reduce(
                    (list, cmd) =>
                    {
                        if (cmd.Visible)
                        {
                            list.push(
                                new LabelCommand({
                                    label: cmd.Label,
                                    tooltip: cmd.Label,
                                    action: $event => this.executeCardCommand(cmd, $event),
                                    getEnabled: () => cmd.Enabled,
                                    getVisible: () => cmd.Visible,
                                }));
                        }
                        return list;
                    },
                    []);
        }

        return this.context?.$$commands;
    }

    private executeCardCommand(command: IInfoCardAction, $event?: Event): void
    {
        $event?.stopPropagation();

        if (this.isSearchTable && !this.commandCard)
        {
            this.fetchCommandCard()
                .then(() =>
                {
                    this.performCardCommandImpl(command);
                });
        }
        else
        {
            this.performCardCommandImpl(command);
        }
    }

    private fetchCommandCard(): Promise<void>
    {
        const criteria: IInfoTableRowActionCriteria = {
            ProviderId: this.providerId,
            RowId: this.row.Id,
            RowIdName: this.row.RowIdName,
            LazyLoadCommands: this.useLazyLoadCommands,
            SerializeEntity: true,
        };
        return this.$infoTableService.GetSearchRowActions(criteria)
            .then(result =>
            {
                this.context.$$commandCard = result;
            });
    }

    private performCardCommandImpl(command: IInfoCardAction): void
    {
        switch (command.Action)
        {
            case "Metadata":
                this.executeMetadataAction(command);
                break;
            case "Client":
                this.executeCustomClientAction(command);
                break;
            case "Server":
                this.executeServerAction(command);
                break;
            default:
                this.executeIntentAction(command);
                break;
        }
    }

    private executeMetadataAction(command: IInfoCardAction): void
    {
        if (EventEmitterHelper.hasSubscribers(this.customCommand)
            && /^(Copy|Edit|Delete)$/.test(command.Data[0]))
        {
            // TODO - dispatch to correct events; "Edit" to this.edit, "Delete" to this.delete, etc.
            this.customCommand.emit({
                command: {
                    Action: command.Data[0]
                },
                rows: [this.row],
            });

            return;
        }

        const metadataAction: IMetadataAction = StringHelper.parse(command.Data[0]);
        if (!this.row.$$model)
        {
            this.row.$$model = this.commandCard?.Entity;
        }

        command.Enabled = false;
        const repositoryFormData = this.getRepositoryFormData(metadataAction, this.row.$$model, this.datacontroller);
        this.$metadataActionService.ExecuteAction(this.row.$$model, metadataAction, repositoryFormData)
            .then(
                MetadataActionHelpers.isReturnPathThis(metadataAction)
                    ? (newModel: AugmentedModelObject & IAugmentedWithModel) =>
                    {
                        if (!newModel)
                        {
                            return undefined;
                        }

                        if (!newModel.IsDeleted)
                        {
                            this.row.$$model = newModel;
                            this.row.Elements.forEach(el =>
                            {
                                el.Value = (newModel[el.Name] as string) || el.Value;
                            });

                            return undefined;
                        }

                        // TODO - what use case is this? why would a metadataAction result in returning a deleted object.
                        const index = this.infoTable.Rows.indexOf(this.row);
                        if (index === -1)
                        {
                            return undefined;
                        }

                        return this.row;
                    }
                    : (_newModel: AugmentedModelObject & IAugmentedWithModel) => undefined,
                (x: unknown) =>
                {
                    let msg = "Unknown error occurred";
                    if (isHttpResponseError(x))
                    {
                        msg = x.error.Message.replace(/(?:\r\n|\r|\n)/g, "<br>");
                    }

                    // TODO - shouldn't we return this promise?
                    this.$commonDialogsService.showAlertHtmlMessage(msg, "Error");
                })
            .then((deletedRow: AugmentedTableRow) =>
            {
                if (deletedRow)
                {
                    this.delete.emit(deletedRow);
                    return;
                }

                this.reloadTableData.emit({
                    forceReload: true,
                    initial: false
                });
            })
            .finally(() =>
            {
                command.Enabled = true;
            });
    }

    private getRepositoryFormData(action: IMetadataAction, model: IAugmentedWithParent, datacontroller: RepositoryFormData): RepositoryFormData
    {
        if (!action)
        {
            return new RepositoryFormData(null, null);
        }

        if (datacontroller)
        {
            return datacontroller;
        }

        const type = action.ViewToShow
            ? this.$metadataViewService.GetTypeName(action.ViewToShow)
            : model.$$TypeName;
        if (type)
        {
            return new RepositoryFormData(type, model);
        }
    }

    private executeCustomClientAction(command: IInfoCardAction): void
    {
        if (this.isSearchTable)
        {
            this.customAction.emit({
                command: command,
                item: this.commandCard,
            });
        }
    }

    private executeServerAction(command: IInfoCardAction): void
    {
        const actionData = {
            Row: this.row,
            ProviderId: this.providerId,
            Action: command,
            UseCardProvider: this.isSearchTable,
        };

        (command.ConfirmAction
            ? this.$commonDialogsService
                .showConfirmationMessage("Are you sure you want to " + command.Label + "?", "Confirm Action")
                .then(() =>
                {
                    return this.$infoTableService.PerformRowAction(actionData);
                })
            : this.$infoTableService.PerformRowAction(actionData))
            .then(result =>
            {
                if (!result)
                {
                    return;
                }

                this.$commonDialogsService.showAlertHtmlMessage(result, "Success");
            });
    }

    private executeIntentAction(command: IInfoCardAction): void
    {
        const params: Record<string, unknown> = {};
        command.Data.forEach((value, key) =>
        {
            if (key % 2 === 0)
            {
                params[value] = command.Data[key + 1];
            }
        });
        this.$intentService.resolve(command.MimeType, command.Action, params);
    }

    onView($event?: Event): void
    {
        $event?.stopPropagation();
        this.onOpen("view");
    }

    onEdit($event?: Event): void
    {
        $event?.stopPropagation();
        this.onOpen("edit");
    }

    private onOpen(type: "view" | "edit"): void
    {
        this.edit.emit({
            row: this.row,
            providerId: this.providerId,
            viewOnly: type === "view",
            canDelete: this.canDelete,
        });
    }

    onDelete($event?: Event): void
    {
        $event?.stopPropagation();
        const msg = this.infoTable.DeleteMessage
            || "Are you sure you want to delete this item?";


        this.$commonDialogsService.showConfirmationMessage(msg, "Delete Item")
            .then(() =>
            {
                this.delete.emit(this.row);
            });
    }

    private canDo(type: "Add" | "Edit" | "Delete"): boolean
    {
        if (!this.infoTable || !this.row)
        {
            return false;
        }

        const staticValue: boolean = type === "Add"
            ? this.infoTable.CanAdd
            : type === "Edit"
                ? this.infoTable.CanEdit
                : this.infoTable.CanDelete;
        const dynamicProperty: string = type === "Add"
            ? undefined // `this.tableData.CanAddProperty` wasn't defined on the ITableData
            : type === "Edit"
                ? this.infoTable.CanEditProperty
                : this.infoTable.CanDeleteProperty;

        const canDo = staticValue && !this.isViewOnly;

        const canDoProp = this.row.Elements.find(x => x.Name === dynamicProperty && !!x.Name);
        if (canDoProp?.Value)
        {
            return /true/.test(canDoProp.Value.toString());
        }

        return canDo;
    }

    performCommand(command: Command, $event?: Event): void
    {
        if (!Utilities.IsNullOrUndefined($event)) {
            $event.stopPropagation();
        }
        this.overFlowMenu.open = false;
        command.action();
    }

    overflowMenu($event: any): void
    {
        $event.preventDefault();
        let elements = document.getElementsByTagName("grid-row-actions");
        if (!elements || elements.length === 0)
            return;
        this.overFlowMenu.anchorElement = <HTMLElement>document.getElementsByTagName("grid-row-actions")[0];
        requestAnimationFrame(() => this.overFlowMenu.open = true);
    }
}
